import { useMinorUnitsToPriceString } from '@apps/card/hooks/useMinorUnitsToPriceString';
import type { StationeryPriceInfoRawLineItem, StationeryProduct } from '@shared/utils/print-price/types';
import { useCallback } from 'react';
import type { PriceSummaryLineItem } from '../PriceSummary.types';
import { PriceSummaryCurrencyFormatOptions } from '../ProductPriceSummary.controller';
import { calculateStationeryPricing } from '@shared/utils/print-price/utils/calculatePrices';
import { useProductAttributes } from '@apps/card/components/ProductUIConfig/useProductAttributes';
import { useShippingMethodLabelsAndDescriptions } from '@apps/card/hooks/useShippingMethodLabelsAndDescriptions';
import { addError } from '@shared/utils/logger';
import { useStationeryProductPricingContext } from '@apps/card/components/ProductUIConfig/utils/useStationeryProductPricingContext';
import { useIsFeatureEnabled } from '@shared/core/featureFlags/featureFlags.provider';
import { CurrencyCode } from '@shared/hooks/useGeoLocale';
import { useCurrencyConversionRate } from '@apps/card/hooks/useCurrencyConversionRate';

// Converts fancy line items to translated Label / Value pairs
export const usePriceCalculatorLineItemsToPriceSummaryLineItems = (product: StationeryProduct, quantity: number) => {
  const attributes = useProductAttributes();
  const formatPrice = useMinorUnitsToPriceString();
  const [isLocalCurrencyEnabled] = useIsFeatureEnabled('printLocalCurrency');

  const { orderCurrencyISO4217: orderCurrency } = useStationeryProductPricingContext(false);

  // If `printLocalCurrency` feature flag is enabled, use the order currency. Otherwise, use `USD`.
  // `undefined` is treated as `USD` in our system.
  const effectiveCurrency = isLocalCurrencyEnabled ? (orderCurrency ?? CurrencyCode.USD) : undefined;
  const { data: conversionRate } = useCurrencyConversionRate(effectiveCurrency);

  const shippingMethodLabelsAndDescriptions = useShippingMethodLabelsAndDescriptions();

  const toLineItems = useCallback(
    (lineItems: StationeryPriceInfoRawLineItem[], stepFilter?: string[], includeQuantity?: boolean): PriceSummaryLineItem[] => {
      const currencyOptions = isLocalCurrencyEnabled && orderCurrency ? { ...PriceSummaryCurrencyFormatOptions, currency: orderCurrency } : PriceSummaryCurrencyFormatOptions;
      return lineItems
        .map(item => {
          switch (item.type) {
            case 'base_cost': {
              // Move to translation + interpolation
              // Replace 'cards' with some string on ProductConfig.
              // for cases like 'napkins,' 'signs,' etc...
              if (stepFilter && !includeQuantity) {
                return null;
              }

              const unitPriceFormatted = formatPrice(item.unitPriceInMinorUnits, currencyOptions);
              const totalPriceFormatted = formatPrice(item.totalPriceInMinorUnits, currencyOptions);
              return [`Quantity: ${item.quantity} items at ${unitPriceFormatted} each`, totalPriceFormatted];
            }
            case 'option': {
              const attribute = attributes.find(attr => attr.attributeId === item.attributeId);
              if (!attribute) return null;

              const option = attribute.options.find(opt => opt.optionId === item.optionId);
              const attributeName = attribute?.lineItem ?? '';
              const optionName = option?.lineItem ?? '';

              if (stepFilter && attribute?.step && !stepFilter.includes(attribute.step)) {
                return null;
              }

              const totalPriceFormatted = formatPrice(item.totalPriceInMinorUnits, currencyOptions);
              return [`${attributeName}${optionName ? `: ${optionName}` : ''}`, totalPriceFormatted];
            }
            case 'delivery': {
              if (stepFilter && !stepFilter.includes('shipping')) {
                return null;
              }

              const deliveryId = item.deliveryMethodId;
              const deliveryConfig = product.shippingOptions?.find(c => c.id === deliveryId);

              if (!deliveryConfig) {
                throw new Error('Invalid delivery config for line item.');
              }

              const deliveryPrice = calculateStationeryPricing(deliveryConfig.pricing, quantity);
              const deliveryPriceConverted =
                isLocalCurrencyEnabled && conversionRate ? deliveryPrice * conversionRate.exchangeRate * conversionRate.taxesAndFeesMultiplier : deliveryPrice;
              const deliveryPriceFormatted = formatPrice(deliveryPriceConverted, currencyOptions);

              const deliveryLabel = shippingMethodLabelsAndDescriptions[deliveryId as keyof typeof shippingMethodLabelsAndDescriptions].title;

              if (!deliveryLabel) {
                addError('Invalid delivery label for line item.', { deliveryId, productId: product.id });
              }

              return [`Shipping: ${deliveryLabel}`, deliveryPriceFormatted, { highlight: deliveryPrice === 0 }];
            }
            case 'tax': {
              if (stepFilter && !stepFilter.includes('salesTax')) {
                return null;
              }

              const taxFormatted = formatPrice(item.totalPriceInMinorUnits, currencyOptions);
              return ['Sales Tax', taxFormatted];
            }
          }
        })
        .filter(lineItem => lineItem !== null) as PriceSummaryLineItem[];
    },
    [attributes, formatPrice, product.shippingOptions, quantity, product.id, shippingMethodLabelsAndDescriptions, isLocalCurrencyEnabled, conversionRate, orderCurrency],
  );

  return toLineItems;
};
